/**
 * GadsExport.js — Self-Service-Export: Wochen × Kampagnen × Berichte (b01–b12c) → ZIP.
 * 4. Bereich „Fragen" (K-spezifisch, editierbar) → optional als <K>_Fragen.csv in die ZIP.
 * Backend: api/gads_export_bxx.php (ZIP) + api/gads_kpi_questions.php (Fragen-CRUD). Bearer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './GadsKpi.css';

const API_BASE = '/api';
const KAMPS = [['VA', 'Verlustangst'], ['EF', 'Eifersucht'], ['EA', 'Emot. Abh.'], ['SO', 'Souveränität'], ['BR', 'Branding']];
const REPORTS = [
  ['b01', 'Kampagne'], ['b02', 'Auktionen'], ['b03', 'Geräte'], ['b04', 'Alter'], ['b05', 'Standorte'],
  ['b06', 'Werbezeitplan'], ['b07', 'Zielgruppen'], ['b08', 'Suchbegriffe'], ['b09', 'Keywords'],
  ['b10', 'Titel'], ['b11', 'Texte'], ['b12', 'Sitelinks'], ['b12a', 'Marke'], ['b12c', 'Bilder'],
];

export default function GadsExport() {
  const [weeks, setWeeks] = useState(null);
  const [selWeeks, setSelWeeks] = useState({});
  const [selK, setSelK] = useState({ VA: true, EF: true, EA: true, SO: false, BR: true });
  const [selR, setSelR] = useState(Object.fromEntries(REPORTS.map(r => [r[0], true])));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Fragen
  const [frCamp, setFrCamp] = useState('VA');
  const [frText, setFrText] = useState({});
  const [selFr, setSelFr] = useState(Object.fromEntries(REPORTS.map(r => [r[0], true])));
  const [frBusy, setFrBusy] = useState(false);
  const [frMsg, setFrMsg] = useState('');
  const [bigTa, setBigTa] = useState({}); // bxx → vergrößert (Doppelklick)
  // Bereich 5 „Was wurde gemacht" (K × Woche)
  const [clCamp, setClCamp] = useState('VA');
  const [clWeek, setClWeek] = useState('');
  const [clData, setClData] = useState({ lp_name: '', k_gads: [], k_manual: '', lp: '' });
  const [clBusy, setClBusy] = useState(false);
  const [clMsg, setClMsg] = useState('');
  const [inclCl, setInclCl] = useState(true); // WasWurdeGemacht.csv ins ZIP
  const [clBig, setClBig] = useState({}); // 'km' | 'lp' → vergrößert

  const token = () => localStorage.getItem('token') || '';

  useEffect(() => {
    fetch(`${API_BASE}/gads_export_bxx.php?list=weeks`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) { setWeeks(j.weeks); setSelWeeks(Object.fromEntries(j.weeks.slice(0, 2).map(w => [w.from, true]))); setClWeek(j.weeks[0]?.from || ''); } else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  const loadChangelog = useCallback((camp, week) => {
    if (!week) return;
    setClMsg('');
    fetch(`${API_BASE}/gads_kpi_changelog.php?campaign=${camp}&week=${week}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setClData({ lp_name: j.lp_name || '', k_gads: j.k_gads || [], k_manual: j.k_manual || '', lp: j.lp || '' }); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadChangelog(clCamp, clWeek); }, [clCamp, clWeek, loadChangelog]);

  const saveChangelog = async () => {
    setClBusy(true); setClMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: clCamp, week: clWeek, k_manual: clData.k_manual, lp: clData.lp }),
      });
      const j = await res.json();
      setClMsg(j.success ? '✔ gespeichert' : ('Fehler: ' + (j.message || '')));
    } catch (e) { setClMsg('Fehler: ' + e); }
    setClBusy(false);
  };

  const loadQuestions = useCallback((camp) => {
    setFrMsg('');
    fetch(`${API_BASE}/gads_kpi_questions.php?campaign=${camp}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setFrText(Object.fromEntries(Object.entries(j.questions).map(([k, arr]) => [k, arr.join('\n')]))); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadQuestions(frCamp); }, [frCamp, loadQuestions]);

  const saveQuestions = async () => {
    setFrBusy(true); setFrMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_questions.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: frCamp, questions: frText }),
      });
      const j = await res.json();
      setFrMsg(j.success ? `✔ ${frCamp}: ${j.saved} Fragen gespeichert` : ('Fehler: ' + (j.message || '')));
    } catch (e) { setFrMsg('Fehler: ' + e); }
    setFrBusy(false);
  };

  const toggle = (setter) => (key) => setter(s => ({ ...s, [key]: !s[key] }));
  const setAllR = v => setSelR(Object.fromEntries(REPORTS.map(r => [r[0], v])));
  const picks = obj => Object.keys(obj).filter(k => obj[k]);

  const doExport = async () => {
    const camps = picks(selK), wks = picks(selWeeks), reps = picks(selR), frs = picks(selFr);
    if (!camps.length) { setErr('Mindestens 1 Kampagne wählen.'); return; }
    if (!reps.length && !frs.length && !inclCl) { setErr('Mindestens 1 Bericht, Fragen oder „Was wurde gemacht" wählen.'); return; }
    if ((reps.length || inclCl) && !wks.length) { setErr('Für Berichte / „Was wurde gemacht" mindestens 1 Woche wählen.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const p = new URLSearchParams({ campaigns: camps.join(','), weeks: wks.join(','), reports: reps.join(','), fragen: frs.join(','), changelog: inclCl ? '1' : '' });
      const res = await fetch(`${API_BASE}/gads_export_bxx.php?${p}`, { headers: { Authorization: 'Bearer ' + token() } });
      if (!res.ok) { const t = await res.text(); throw new Error('HTTP ' + res.status + ' ' + t.slice(0, 120)); }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || 'GAds_Export.zip';
      const nf = res.headers.get('X-Files'), nr = res.headers.get('X-Rows');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
      setMsg(`✔ ${fname} — ${nf || '?'} Dateien · ${nr || '?'} Zeilen`);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const chk = (on) => 'gk-kchk' + (on ? ' on' : '');

  return (
    <div className="gk-wrap">
      <Link to="/admin" className="gk-back">← Zurück zum Dashboard</Link>
      <h2 className="gk-title">⬇ GADS Export <small>Wochen · Kampagnen · Berichte · Fragen → ZIP (CSV)</small></h2>

      <div className="gx-grid">
        <section className="gk-section">
          <div className="gk-head" style={{ cursor: 'default' }}><span>1 · Kampagnen</span></div>
          <div className="gx-body">
            {KAMPS.map(([c, full]) => (
              <label key={c} className={chk(selK[c])}><input type="checkbox" checked={!!selK[c]} onChange={() => toggle(setSelK)(c)} /><span>{c} <small>{full}</small></span></label>
            ))}
          </div>
        </section>

        <section className="gk-section">
          <div className="gk-head" style={{ cursor: 'default' }}><span>2 · Berichte</span>
            <span><button className="gk-kbtn" onClick={() => setAllR(true)}>Alle an</button> <button className="gk-kbtn" onClick={() => setAllR(false)}>Alle ab</button></span>
          </div>
          <div className="gx-body">
            {REPORTS.map(([k, lbl]) => (
              <label key={k} className={chk(selR[k])}><input type="checkbox" checked={!!selR[k]} onChange={() => toggle(setSelR)(k)} /><span>{k} <small>{lbl}</small></span></label>
            ))}
          </div>
        </section>

        <section className="gk-section">
          <div className="gk-head" style={{ cursor: 'default' }}><span>3 · Wochen (Fr–Do)</span></div>
          <div className="gx-body gx-weeks">
            {!weeks ? <span className="gk-placeholder">lädt…</span> : weeks.map(w => (
              <label key={w.from} className={chk(selWeeks[w.from])}><input type="checkbox" checked={!!selWeeks[w.from]} onChange={() => toggle(setSelWeeks)(w.from)} /><span>{w.label}</span></label>
            ))}
          </div>
        </section>
      </div>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>4 · Fragen <small style={{ fontWeight: 400 }}>(K-spezifisch — für die b13-Auswertung durch CD)</small></span>
          <span className="gx-frtop">
            Kampagne:&nbsp;
            <select className="gx-select" value={frCamp} onChange={e => setFrCamp(e.target.value)}>
              {KAMPS.map(([c, full]) => <option key={c} value={c}>{c} · {full}</option>)}
            </select>
            <button className="gk-kbtn" onClick={saveQuestions} disabled={frBusy}>{frBusy ? '⏳' : '💾 Fragen speichern'}</button>
            {frMsg && <span className="gx-frmsg">{frMsg}</span>}
          </span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">Häkchen links = diese bxx-Fragen kommen in <code>{frCamp}_Fragen.csv</code>. Eine Frage pro Zeile. <strong>Doppelklick</strong> auf ein Textfeld = vergrößern / wieder verkleinern. Vor Kampagnenwechsel speichern.</div>
          <div className="gx-frscroll">
            {REPORTS.map(([k, lbl]) => (
              <div className="gx-frrow" key={k}>
                <label className="gx-frchk"><input type="checkbox" checked={!!selFr[k]} onChange={() => toggle(setSelFr)(k)} /> <strong>{k}</strong> <span className="gx-frlbl">{lbl}</span></label>
                <textarea className={'gx-frta' + (bigTa[k] ? ' gx-frta-big' : '')} rows={bigTa[k] ? 16 : 2}
                  onDoubleClick={() => setBigTa(s => ({ ...s, [k]: !s[k] }))}
                  placeholder="(noch keine Fragen)" value={frText[k] || ''} onChange={e => setFrText(t => ({ ...t, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>5 · Was wurde gemacht <small style={{ fontWeight: 400 }}>(pro Kampagne × Woche — Kontext für CD: hausgemacht oder Markt?)</small></span>
          <span className="gx-frtop">
            <select className="gx-select" value={clCamp} onChange={e => setClCamp(e.target.value)}>
              {KAMPS.map(([c, full]) => <option key={c} value={c}>{c} · {full}</option>)}
            </select>
            <select className="gx-select" value={clWeek} onChange={e => setClWeek(e.target.value)}>
              {(weeks || []).map(w => <option key={w.from} value={w.from}>{w.label}</option>)}
            </select>
            <button className="gk-kbtn" onClick={saveChangelog} disabled={clBusy}>{clBusy ? '⏳' : '💾 Speichern'}</button>
            {clMsg && <span className="gx-frmsg">{clMsg}</span>}
          </span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">
            <label className="gx-clincl"><input type="checkbox" checked={inclCl} onChange={() => setInclCl(v => !v)} /> <strong>„Was wurde gemacht" als <code>&lt;K&gt;_WasWurdeGemacht.csv</code> ins ZIP</strong> (für alle gewählten Kampagnen × Wochen)</label>
            <div>GADS-Log = automatisch (nur lesen). Manuelle K- und LP-Notizen ergänzen, was der Log nicht zeigt. <strong>Doppelklick</strong> = Textfeld vergrößern.</div>
          </div>
          <div className="gx-clrow">
            <label className="gx-cllbl">K-Änderungen · GADS-Log <small>(automatisch · {clData.k_gads.length})</small></label>
            <textarea className="gx-frta gx-clro" readOnly rows={4}
              value={clData.k_gads.map(g => (g.zeit || '').slice(0, 16) + ' · ' + g.text).join('\n')}
              placeholder="(keine Log-Einträge in dieser Woche)" />
          </div>
          <div className="gx-clrow">
            <label className="gx-cllbl">K-Änderungen · manuell</label>
            <textarea className={'gx-frta' + (clBig.km ? ' gx-frta-big' : '')} rows={clBig.km ? 12 : 3}
              onDoubleClick={() => setClBig(s => ({ ...s, km: !s.km }))}
              placeholder="z. B. Budget bewusst gedeckelt, weil …" value={clData.k_manual}
              onChange={e => setClData(d => ({ ...d, k_manual: e.target.value }))} />
          </div>
          <div className="gx-clrow">
            <label className="gx-cllbl">LP-Änderungen <small>({clData.lp_name})</small></label>
            <textarea className={'gx-frta' + (clBig.lp ? ' gx-frta-big' : '')} rows={clBig.lp ? 12 : 3}
              onDoubleClick={() => setClBig(s => ({ ...s, lp: !s.lp }))}
              placeholder="z. B. Hero-Headline geändert, neuer CTA …" value={clData.lp}
              onChange={e => setClData(d => ({ ...d, lp: e.target.value }))} />
          </div>
        </div>
      </section>

      <div className="gx-actions">
        <button className="gk-kbtn gx-go" onClick={doExport} disabled={busy}>{busy ? '⏳ erstelle ZIP…' : '⬇ ZIP erstellen'}</button>
        <span className="gk-toolbar-hint">{picks(selK).length} K · {picks(selWeeks).length} Wochen · {picks(selR).length} Berichte · {picks(selFr).length} Fragen-Sets{inclCl ? ' · +WasWurdeGemacht' : ''}</span>
      </div>
      {err && <div className="gk-err">Fehler: {err}</div>}
      {msg && <div className="gx-ok">{msg}</div>}
      <p className="gk-toolbar-hint" style={{ marginTop: 12 }}>Pro Kampagne+Bericht eine CSV + optional <code>&lt;K&gt;_Fragen.csv</code> (Semikolon, dt. Komma, UTF-8). ZIP heißt bei 1 K nach ihr, sonst „NK".</p>
    </div>
  );
}
